import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";

import { subCheckmark, subCross } from "../../../designSystemIcons";

const subscriptionDetails: {
  key:
    | "sbcSolver"
    | "myEvolutions"
    | "myClub"
    | "squadBuilder"
    | "recommendations";
  subscription: string[];
}[] = [
  { key: "sbcSolver", subscription: ["Elite", "Ultimate"] },
  { key: "myEvolutions", subscription: ["Elite", "Ultimate"] },
  { key: "myClub", subscription: ["Elite", "Ultimate"] },
  { key: "squadBuilder", subscription: ["Ultimate"] },
  { key: "recommendations", subscription: ["Ultimate"] },
];

const TIER_KEY = { Pro: "pro", Elite: "elite", Ultimate: "ultimate" } as const;

type SubscriptionCardProps = {
  tier: "Elite" | "Pro" | "Ultimate";
  selected?: boolean;
  activeSubscription?: boolean;
  monthlyPrice: number;
  onClick: (activeSubView: "Elite" | "Pro" | "Ultimate") => void;
  mostPopular?: boolean;
  annualDiscount?: number;
  yearlyPrice?: number;
  currency?: string;
};

const SubscriptionCard = ({
  tier,
  selected,
  activeSubscription,
  monthlyPrice,
  onClick,
  mostPopular,
  annualDiscount,
  yearlyPrice,
  currency,
}: SubscriptionCardProps) => {
  const { t } = useTranslation("subscription");
  return (
    <button
      onClick={() => onClick(tier as any)}
      className={cn(
        "flex flex-col px-4 py-4 md-lg:py-8 bg-white rounded-lg w-[308px] relative will-change-transform ",
        selected
          ? "outline outline-4 outline-primary-blue opacity-100 text-gray-800"
          : "opacity-90 text-light-secondary",
        selected
          ? "-translate-y-1.5"
          : "transition hover:-translate-y-1.5 duration-200",
      )}
    >
      {(mostPopular || activeSubscription) && (
        <p className="absolute top-0 text-sm touch-device:text-base font-medium text-white transform -translate-x-1/2 -translate-y-1/2 left-1/2 px-2 py-0.5 bg-primary-blue rounded">
          {activeSubscription ? t("card.activePlan") : t("card.mostPopular")}
        </p>
      )}
      {annualDiscount && (
        <p
          className={cn(
            "absolute bottom-0 text-sm touch-device:text-base font-medium text-white transform -translate-x-1/2 translate-y-1/2 left-1/2 px-2 py-0.5 rounded",
            selected ? "bg-primary-blue" : "bg-primary-washed-blue",
          )}
        >
          {t("card.save", { amount: annualDiscount, currency })}
        </p>
      )}
      <h3 className="mx-auto text-2xl font-semibold md-xs:text-3xl">
        {t(`tiers.${TIER_KEY[tier]}` as const)}
      </h3>
      <span
        className={cn(
          "mx-auto font-medium",
          selected ? "text-gray-800" : "text-light-secondary",
          !monthlyPrice && "invisible",
        )}
      >
        <span className="text-lg font-semibold md-xs:text-xl">
          {currency}
          {monthlyPrice}
        </span>
        <span>{t("perMonth")}</span>
      </span>
      <p
        className={cn(
          "text-xs text-gray-500 mx-auto",
          annualDiscount ? "visible" : "invisible",
        )}
      >
        {t("card.billedYearly", { currency, price: yearlyPrice })}
      </p>

      <div className="flex px-4 mt-2 md-lg:mt-4 gap-x-[15px]">
        {subCheckmark}
        <span className="text-base font-semibold md-xs:text-lg">
          {t("card.noAds")}
        </span>
      </div>
      <div
        className={cn(
          "relative flex flex-col px-4 py-2 mt-2 border-2 rounded gap-y-2 md-lg:gap-y-4 w-full",
          tier === "Pro" ? "border-gray-700/50" : "border-gray-700",
        )}
      >
        <p
          className={cn(
            "absolute top-0 right-0 font-medium py-0.5 px-1 rounded-bl-sm text-white text-sm",
            tier === "Pro" ? "bg-gray-700/50" : "bg-gray-700",
          )}
        >
          {t("card.clubImport")}
        </p>

        {subscriptionDetails.map((content, index) => (
          <div
            key={index}
            className={cn(
              "flex gap-x-[15px]",
              !content.subscription.includes(tier) && "opacity-60",
            )}
          >
            <div className="my-auto">
              {content.subscription.includes(tier) ? subCheckmark : subCross}
            </div>
            <div className={cn("flex flex-col text-left")}>
              <span className="text-base font-semibold md-xs:text-lg">
                {t(`card.details.${content.key}.title` as const)}
              </span>
              <p className={cn("text-xs text-light-secondary")}>
                {t(`card.details.${content.key}.info` as const)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </button>
  );
};

export default SubscriptionCard;
